import { IPurchasable } from "@smartrr/shared/entities/Purchasable";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { getPurchasableFromPurchasableMap } from "@smartrr/shared/utils/useVariantToPurchasableMap";
import clone from 'lodash/clone';
import isNumber from 'lodash/isNumber';

import { IGetPrice } from "./views/OperationAquarius/utils/Pricing";

export interface IUpcomingCartLineItem {
  id?: string;
  quantity: number;
  isAddOn: boolean;
  variantShopifyId?: string;
  sellingPlanShopifyId?: string | null;
  estimatedPrice: IGetPrice;
}
export interface IUpcomingCartValidationResult {
  isValid: boolean;
  errorMessage?: string;
}
type UpcomingCartValidationCallback = (lineItems: IUpcomingCartLineItem[]) => IUpcomingCartValidationResult;

type NavigationBarCreatedCallback = (nav: HTMLElement | null) => void;

export interface IValidatePurchasablesResult {
  shopifyId?: string;
  maxQuantity?: number;
}

interface IValidatePurchasables {
  purchaseState: IPurchaseState;
  subscriptionPurchasables: Record<string, IPurchasable>;
  addOnPurchasables: IPurchasable[];
}

type ValidatePurchasablesCallback = (info: IValidatePurchasables) => IValidatePurchasablesResult[] | undefined;

/**
 * @property hiddenProducts - Shopify IDs of products that shouldn't be displayed on Add/Swap screen.
 * @property upcomingCartValidation - callback that validates cart changes that customer tries to perform
 * in an upcoming order.
 * @property navigationBarCreatedCallback - callback when the Navigation bar is first rendered
 * @property validatePurchasablesCallback - callback when Addons modal is displayed to hide/set maximum for each of the possible purchasables
 */
interface ISmartrrAccountConfig {
  hiddenProducts?: string[];
  upcomingCartValidationCallback?: UpcomingCartValidationCallback;
  navigationBarCreatedCallback?: NavigationBarCreatedCallback;
  validatePurchasablesCallback?: ValidatePurchasablesCallback;
}

export const smartrrAccountConfig: ISmartrrAccountConfig = {
  get hiddenProducts() {
    return (window as any)?.smartrrAccountConfig?.hiddenProducts;
  },
  get upcomingCartValidationCallback() {
    return (window as any)?.smartrrAccountConfig?.upcomingCartValidationCallback;
  },
  get navigationBarCreatedCallback() {
    return (window as any)?.smartrrAccountConfig?.navigationBarCreatedCallback;
  },
  get validatePurchasablesCallback() {
    return (window as any)?.smartrrAccountConfig?.validatePurchasablesCallback;
  },
};

/**
 * @property getMaxQuantity - return maximum amount that can be added for each of the purchasables present in the AddOns list
 * @property isValidatedAddOn - returns true if nothing was specified for the purchasable or if a valid max amount was sent over
 */
export interface IHandleValidatePurchasableCallback {
  getMaxQuantity: (shopifyId: string) => number;
  isValidatedAddOn: (shopifyId: string) => boolean;
  validatedPurchasables: IValidatePurchasablesResult[];
}

export const handleValidatingPurchasables = (
  purchaseState: IPurchaseState,
  preFilteredPurchasables: IPurchasable[],
  variantMap: { [id: string]: IPurchasable }
): IHandleValidatePurchasableCallback => {
  const subscriptionPurchasables: Record<string, IPurchasable> = {};

  for (const item of purchaseState.stLineItems) {
    const purchasable = getPurchasableFromPurchasableMap(variantMap, item.vnt) || undefined;

    if (purchasable && item.vnt.shopifyId) {
      subscriptionPurchasables[item.vnt.shopifyId] = purchasable;
    }
  }

  const callbackData: IValidatePurchasables = {
    purchaseState: clone(purchaseState),
    subscriptionPurchasables,
    addOnPurchasables: clone(preFilteredPurchasables),
  };

  const validatedPurchasables = smartrrAccountConfig.validatePurchasablesCallback
    ? smartrrAccountConfig.validatePurchasablesCallback(callbackData) || []
    : [];

  const getMaxQuantity = (shopifyId: string) => {
    let maxQuantity = -1;
    for (const validated of validatedPurchasables) {
      if (validated?.shopifyId && validated.shopifyId === shopifyId) {
        if (validated.maxQuantity == undefined || !isNumber(validated.maxQuantity)) {
          maxQuantity = -1;
        } else {
          maxQuantity = Math.abs(validated.maxQuantity);
        }
      }
    }
    return maxQuantity;
  };

  const isValidatedAddOn = (shopifyId: string) => {
    const validation = getMaxQuantity(shopifyId);
    return validation !== 0;
  };

  return {
    getMaxQuantity,
    isValidatedAddOn,
    validatedPurchasables,
  };
};
